<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref, watch } from 'vue';

import TableLoader from '@/components/loaders/TableLoader.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import * as commonConstants from '@/constants/common';

export interface Props {
  printers?: openapi.UserPrinterOptionsContentOpenapi;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  printers: () => [],
  loading: false,
});
const emit = defineEmits<{
  selectPrinter: [value: number];
}>();

const selectedPrinterId = ref(NaN);

function handleTrClick(printerId: number): void {
  selectedPrinterId.value = printerId;
  emit('selectPrinter', printerId);
}

watch(
  () => props.printers,
  (newData, oldData) => {
    if (props.printers.length === 1) {
      handleTrClick(props.printers[0].id);
    }
  },
  { immediate: true },
);
</script>

<template>
  <table data-testid="printersCustomTable" :class="$style.printersCustomTable">
    <thead>
      <tr>
        <th :class="$style.nameTh">Name</th>
        <th :class="$style.stateTh">State</th>
        <th :class="$style.locationTh">Location</th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="3" class="!h-24 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!props.printers?.length">
      <td :colspan="3" class="border-b-0 p-0">
        <TableEmptyText>No printers found</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <tr
        v-for="printer in props.printers"
        :key="printer.id"
        :class="printer.id === selectedPrinterId && $style.selectedTr"
        @click="handleTrClick(printer.id)"
      >
        <td>{{ printer.name || commonConstants.EM_DASH }}</td>
        <td>{{ printer.state || commonConstants.EM_DASH }}</td>
        <td>{{ printer.location || commonConstants.EM_DASH }}</td>
      </tr>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.printersCustomTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  color: colors.$color-text-primary;
}
  .printersCustomTable > tbody > tr {
    &:hover > td {
      background-color: colors.$color-brand-50;
      cursor: pointer;
    }
    &.selectedTr > td {
      background-color: colors.$color-brand-200;
    }
  }
    .printersCustomTable > thead > tr > th {
      font-weight: 500;
      color: colors.$color-text-tertiary;
      border-bottom: 1px solid colors.$color-gray-300;
      padding: 13px 32px 13px 32px;
    }
    .nameTh {
      min-width: 160px;
      width: 100%;
    }
    .stateTh {
    }
    .locationTh {
      min-width: 160px;
      width: 160px;
    }
    .printersCustomTable > tbody > tr > td {
      padding: 9px 32px 9px 32px;
      vertical-align: baseline;
    }
</style>
